import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Row, Col, Spinner, Badge, Container, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import { FaArrowLeft, FaEdit, FaTrash } from "react-icons/fa";

const WarehouseView = () => {
  const navigate = useNavigate();
  const { pointindex, id } = useParams();
  const [warehouseData, setWarehouseData] = useState({
    branch: null,
    warehousename: "",
    warehousetype: "",
    contactperson: "",
    email: "",
    mobileno: "",
    address: "",
    zipcode: "",
    country: null,
    state: null,
    city: null,
    statecode: ""
  });

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}warehouse/findById/${id}`, { headers: authHeader() });
        const warehouse = response.data;

        setWarehouseData({
          branch: warehouse.branch ? {
            value: warehouse.branch.id,
            label: warehouse.branch.branchname
          } : null,
          warehousename: warehouse.warehousename,
          warehousetype: warehouse.warehousetype,
          contactperson: warehouse.contactperson,
          email: warehouse.email,
          mobileno: warehouse.mobileno,
          address: warehouse.address,
          zipcode: warehouse.zipcode,
          country: warehouse.country ? {
            value: warehouse.country.id,
            label: warehouse.country.countryname
          } : null,
          state: warehouse.state ? {
            value: warehouse.state.id,
            label: warehouse.state.statename,
            code: warehouse.state.statecode
          } : null,
          city: warehouse.city ? {
            value: warehouse.city.id,
            label: warehouse.city.cityname
          } : null,
          statecode: warehouse.statecode || (warehouse.state ? warehouse.state.statecode : "")
        });

      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch warehouse data. Please try again.',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const getItem = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(BASE_URL + "warehouse/delete/" + id, {
          headers: authHeader(),
        })
          .then(response => {
            if (response.data.length === 0) {
              Swal.fire(
                'Warning',
                'Warehouse present in another module. Delete operation has been cancelled.',
                'warning'
              );
            } else {
              navigate(`/warehouseindex/${pointindex}`);
              Swal.fire(
                'Deleted!',
                'Warehouse deleted successfully.',
                'success'
              );
            }
          })
          .catch((error) => {
            Swal.fire(
              'Error',
              error.response.data,
              'error'
            );
          });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="p-3">
      {/* Header Section */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold text-warning mb-0">WAREHOUSE - VIEW</h5>
            <div>
              <Button variant="outline-danger" size="sm" className="me-2" onClick={() => getItem(id)}>
                <FaTrash className="me-1" />
                Delete
              </Button>
              <Link to={`/warehouseEdit/${id}/${pointindex}`} className="btn btn-outline-warning btn-sm me-2">
                <FaEdit className="me-1" />
                Edit
              </Link>
              <Link to={`/warehouseindex/${pointindex}`} className="btn btn-secondary btn-sm">
                <FaArrowLeft className="me-1" />
                Back
              </Link>
            </div>
          </div>

          <Row className="g-2">
            <Col md={3}>
              <div className="border-start border-3 border-warning ps-2">
                <small className="text-muted">Warehouse Name</small>
                <div className="fw-bold">{warehouseData.warehousename || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-primary ps-2">
                <small className="text-muted">Branch</small>
                <div className="fw-bold">{warehouseData.branch?.label || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-info ps-2">
                <small className="text-muted">Warehouse Type</small>
                <div className="fw-bold">{warehouseData.warehousetype || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-success ps-2">
                <small className="text-muted">Status</small>
                <div>
                  <Badge bg="success" className="px-2 py-1">Active</Badge>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Basic Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Basic Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Warehouse Name</small>
              <div className="fw-bold">{warehouseData.warehousename || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Warehouse Type</small>
              <div className="fw-bold">{warehouseData.warehousetype || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Branch</small>
              <div className="fw-bold">{warehouseData.branch?.label || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Contact Person</small>
              <div className="fw-bold">{warehouseData.contactperson || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Email</small>
              <div className="fw-bold">{warehouseData.email || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Phone No</small>
              <div className="fw-bold">{warehouseData.mobileno || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Address Details */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Address Details</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={12}>
              <small className="text-muted">Address</small>
              <div className="fw-bold">{warehouseData.address || '-'}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Country</small>
              <div className="fw-bold">{warehouseData.country?.label || '-'}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">State</small>
              <div className="fw-bold">{warehouseData.state?.label || '-'}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">State Code</small>
              <div className="fw-bold">{warehouseData.statecode || '-'}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">City</small>
              <div className="fw-bold">{warehouseData.city?.label || '-'}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Zip Code</small>
              <div className="fw-bold">{warehouseData.zipcode || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="text-center mt-3 pt-2 border-top">
        <Link to={`/warehouseEdit/${id}/${pointindex}`} className="btn btn-warning btn-sm me-2">
          <FaEdit className="me-1" />
          Edit Warehouse
        </Link>
        <Link to={`/warehouseindex/${pointindex}`} className="btn btn-secondary btn-sm">
          <FaArrowLeft className="me-1" />
          Back to List
        </Link>
      </div>
    </Container>
  );
};

export default WarehouseView;